const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api";

const handleResponse = async (response) => {
  const contentType = response.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const data = isJson
    ? await response.json()
    : {
        success: false,
        message: `Unexpected ${response.status} response from server`,
      };

  if (!response.ok && response.status !== 404) {
    // Return the response data so the frontend can access error messages
    return data;
  }

  return data;
};

export const api = {
  // Auth endpoints
  login: async (data) => {
    const res = await fetch(`${BASE_URL}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    });
    return handleResponse(res);
  },

  register: async (data) => {
    const res = await fetch(`${BASE_URL}/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    });
    return handleResponse(res);
  },

  logout: async () => {
    const res = await fetch(`${BASE_URL}/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
    return handleResponse(res);
  },

  // User endpoints
  getMe: async () => {
    const res = await fetch(`${BASE_URL}/users/me`, {
      credentials: "include",
    });
    return handleResponse(res);
  },

  updateMe: async (data) => {
    const res = await fetch(`${BASE_URL}/users/me`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    });
    return handleResponse(res);
  },

  getAddresses: async () => {
    const res = await fetch(`${BASE_URL}/users/addresses`, {
      credentials: "include",
    });
    return handleResponse(res);
  },

  addAddress: async (data) => {
    const res = await fetch(`${BASE_URL}/users/addresses`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    });
    return handleResponse(res);
  },

  deleteAddress: async (addressId) => {
    const res = await fetch(`${BASE_URL}/users/addresses/${addressId}`, {
      method: "DELETE",
      credentials: "include",
    });
    return handleResponse(res);
  },

  // Product endpoints
  getProducts: async () => {
    const res = await fetch(`${BASE_URL}/products`, {
      credentials: "include",
    });
    return handleResponse(res);
  },

  getProduct: async (id) => {
    const res = await fetch(`${BASE_URL}/products/${id}`, {
      credentials: "include",
    });
    return handleResponse(res);
  },

  getMyProducts: async () => {
    const res = await fetch(`${BASE_URL}/products/my`, {
      credentials: "include",
    });
    return handleResponse(res);
  },

  createProduct: async (data) => {
    const res = await fetch(`${BASE_URL}/products`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    });
    return handleResponse(res);
  },

  updateProduct: async (id, data) => {
    const res = await fetch(`${BASE_URL}/products/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    });
    return handleResponse(res);
  },

  deleteProduct: async (id) => {
    const res = await fetch(`${BASE_URL}/products/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    return handleResponse(res);
  },

  uploadImages: async (files) => {
    const formData = new FormData();
    files.forEach((file) => formData.append("images", file));

    const res = await fetch(`${BASE_URL}/upload`, {
      method: "POST",
      credentials: "include",
      body: formData,
    });
    return handleResponse(res);
  },

  // Cart endpoints
  getCart: async () => {
    const res = await fetch(`${BASE_URL}/cart`, {
      credentials: "include",
    });
    return handleResponse(res);
  },

  addToCart: async (productId, quantity, variantName = "") => {
    const res = await fetch(`${BASE_URL}/cart/add`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ productId, quantity, variantName }),
    });
    return handleResponse(res);
  },

  removeFromCart: async (productId, variantName = "") => {
    const res = await fetch(`${BASE_URL}/cart/remove`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ productId, variantName }),
    });
    return handleResponse(res);
  },

  updateCartQuantity: async (productId, quantity, variantName = "") => {
    const res = await fetch(`${BASE_URL}/cart/update-quantity`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ productId, quantity, variantName }),
    });
    return handleResponse(res);
  },

  toggleCartItemSelection: async (productId, selected, variantName = "") => {
    const res = await fetch(`${BASE_URL}/cart/toggle-selected`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ productId, selected, variantName }),
    });
    return handleResponse(res);
  },

  clearCart: async () => {
    const res = await fetch(`${BASE_URL}/cart/clear`, {
      method: "POST",
      credentials: "include",
    });
    return handleResponse(res);
  },

  // Order endpoints
  placeOrder: async (data) => {
    const res = await fetch(`${BASE_URL}/orders`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    });
    return handleResponse(res);
  },

  getOrders: async () => {
    const res = await fetch(`${BASE_URL}/orders`, {
      credentials: "include",
    });
    return handleResponse(res);
  },

  getOrder: async (id) => {
    const res = await fetch(`${BASE_URL}/orders/${id}`, {
      credentials: "include",
    });
    return handleResponse(res);
  },

  cancelOrder: async (id) => {
    const res = await fetch(`${BASE_URL}/orders/${id}/cancel`, {
      method: "PUT",
      credentials: "include",
    });
    return handleResponse(res);
  },

  deleteOrder: async (id) => {
    const res = await fetch(`${BASE_URL}/orders/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    return handleResponse(res);
  },

  // Seller order endpoints
  getSellerOrders: async () => {
    const res = await fetch(`${BASE_URL}/orders/seller`, {
      credentials: "include",
    });
    return handleResponse(res);
  },

  // Admin endpoints
  getAdminStats: async () => {
    const res = await fetch(`${BASE_URL}/admin/stats`, {
      credentials: "include",
    });
    return handleResponse(res);
  },

  getAdminUsers: async () => {
    const res = await fetch(`${BASE_URL}/admin/users`, {
      credentials: "include",
    });
    return handleResponse(res);
  },

  deleteAdminUser: async (id) => {
    const res = await fetch(`${BASE_URL}/admin/users/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    return handleResponse(res);
  },

  getAdminProducts: async () => {
    const res = await fetch(`${BASE_URL}/admin/products`, {
      credentials: "include",
    });
    return handleResponse(res);
  },

  getAdminProduct: async (id) => {
    const res = await fetch(`${BASE_URL}/admin/products/${id}`, {
      credentials: "include",
    });
    return handleResponse(res);
  },

  deleteAdminProduct: async (id) => {
    const res = await fetch(`${BASE_URL}/admin/products/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    return handleResponse(res);
  },

  getAdminOrders: async () => {
    const res = await fetch(`${BASE_URL}/admin/orders`, {
      credentials: "include",
    });
    return handleResponse(res);
  },

  getAdminOrder: async (id) => {
    const res = await fetch(`${BASE_URL}/admin/orders/${id}`, {
      credentials: "include",
    });
    return handleResponse(res);
  },

  updateAdminOrderStatus: async (id, status) => {
    const res = await fetch(`${BASE_URL}/orders/${id}/status`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ status }),
    });
    return handleResponse(res);
  },

  deleteAdminOrder: async (id) => {
    const res = await fetch(`${BASE_URL}/admin/orders/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    return handleResponse(res);
  },
};
